import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '../styles/colors';

const DemoContainer = styled.section`
  padding: 80px 0;
  background: ${colors.backgroundSecondary};
`;

const DemoContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 800;
  color: ${colors.textPrimary};
  text-align: center;
  margin-bottom: 16px;
  line-height: 1.2;
`;

const SectionSubtitle = styled.p`
  font-size: clamp(16px, 2.5vw, 20px);
  color: ${colors.textSecondary};
  text-align: center;
  margin-bottom: 60px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const DemoLayout = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 60px;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const PhoneDemo = styled.div`
  display: flex;
  justify-content: center;
`;

const PhoneMockup = styled.div`
  width: 375px;
  height: 618px;
  background: white;
  border-radius: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  border: 8px solid #1a1a1a;
  position: relative;
  overflow: hidden;
`;

const PhoneScreen = styled.div`
  width: 100%;
  height: 100%;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StatusBar = styled.div`
  height: 44px;
  background: #000;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  color: white;
  font-size: 14px;
  font-weight: 600;
`;

const Time = styled.span``;

const StatusIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BatteryIcon = styled.div`
  width: 24px;
  height: 12px;
  border: 1px solid white;
  border-radius: 2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    right: -3px;
    top: 3px;
    width: 2px;
    height: 6px;
    background: white;
    border-radius: 0 1px 1px 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 2px;
    top: 2px;
    width: 16px;
    height: 8px;
    background: white;
    border-radius: 1px;
  }
`;

const NavigationBar = styled.div`
  padding: 16px 20px 12px;
  background: white;
  border-bottom: 1px solid ${colors.borderLight};
`;

const TabContainer = styled.div`
  display: flex;
  gap: 32px;
`;

const Tab = styled.div<{ active: boolean }>`
  padding-bottom: 8px;
  border-bottom: 2px solid ${props => props.active ? colors.primary : 'transparent'};
`;

const TabText = styled.span<{ active: boolean }>`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.active ? colors.primary : colors.textSecondary};
`;

const ContentContainer = styled.div`
  flex: 1;
  padding: 20px;
  gap: 40px;
  overflow-y: auto;
`;

const SectionTitleMobile = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin-bottom: 16px;
`;

const MentorQuoteCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const MentorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const MentorAvatar = styled.div`
  font-size: 24px;
`;

const MentorName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.textPrimary};
`;

const QuoteText = styled.p`
  font-size: 14px;
  color: ${colors.textSecondary};
  line-height: 1.5;
  font-style: italic;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const SummaryCardTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin-bottom: 16px;
`;

const SummaryItem = styled.div`
  font-size: 14px;
  color: ${colors.textSecondary};
  line-height: 1.5;
  margin-bottom: 12px;
`;

const Divider = styled.div`
  height: 1px;
  background: ${colors.borderLight};
  margin: 12px 0;
`;

const GestureBar = styled.div`
  height: 34px;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: 1px solid ${colors.borderLight};
`;

const GestureIndicator = styled.div`
  width: 134px;
  height: 5px;
  background: #d1d5db;
  border-radius: 3px;
`;

const DemoControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 50px;
  justify-content: center;
  height: 100%;
`;

const ControlTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin-bottom: 10px;
  line-height: 1.3;
`;

const MentorSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
`;

const MentorButton = styled.button<{ active: boolean }>`
  padding: 24px 20px;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 600;
  border: 2px solid ${props => props.active ? colors.primary : colors.borderLight};
  background: ${props => props.active ? colors.primary : 'white'};
  color: ${props => props.active ? 'white' : colors.textPrimary};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  position: relative;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const MentorButtonTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1.3;
`;

const MentorButtonDescription = styled.div<{ active: boolean }>`
  font-size: 15px;
  font-weight: 500;
  color: ${props => props.active ? 'rgba(255, 255, 255, 0.9)' : colors.textSecondary};
  line-height: 1.4;
  opacity: 0.95;
`;

const mentors = [
    { id: 'buffett', name: 'AI 멘토 워렌 버핏', icon: '🧓', quote: '"가치투자의 핵심은 좋은 기업을 좋은 가격에 사는 것이다. 시장의 단기적 변동성은 무시하고 기업의 본질적 가치에 집중한다."', shortDesc: '가치투자의 아버지, 장기적 관점의 투자 철학' },
    { id: 'graham', name: 'AI 멘토 벤저민 그레이엄', icon: '📘', quote: '"안전마진을 충분히 확보하는 것이 투자의 기본 원칙이다. 내재가치 대비 저평가된 기업을 찾아 장기 보유한다."', shortDesc: '안전마진의 창시자, 위험 관리 중심 투자' },
    { id: 'wood', name: 'AI 멘토 캐시 우드', icon: '🚀', quote: '"혁신 기술의 성장 잠재력을 과소평가하지 않는다. AI, 바이오, 신재생에너지 등 미래 기술에 대한 투자는 장기적 관점이 필요하다."', shortDesc: '혁신 기술 전문가, 미래 성장 주도' },
    { id: 'munger', name: 'AI 멘토 찰리 멍거', icon: '🧠', quote: '"투자는 다학제적 사고를 요구한다. 심리학, 철학, 역사 등 다양한 분야의 지식을 종합하여 투자 기회를 발견한다."', shortDesc: '다학제적 사고의 철학자, 통찰력 중심' },
    { id: 'dalio', name: 'AI 멘토 레이 달리오', icon: '🌎', quote: '"세계 경제는 하나의 거대한 시스템이다. 각국의 정책, 통화, 무역 관계를 종합적으로 분석하여 포트폴리오를 분산한다."', shortDesc: '글로벌 매크로 전문가, 시스템 사고' },
    { id: 'lynch', name: 'AI 멘토 피터 린치', icon: '🔍', quote: '"투자 기회는 일상에서 발견된다. 소비자로서의 경험과 관찰이 투자자로서의 통찰력을 만들어낸다."', shortDesc: '일상 관찰의 대가, 소비자 중심 투자' }
];

const summaryData = {
    buffett: [
        '현재 삼성전자는 단기 실적 부진에 따른 외국인 매도세와 심리적 불안이 뚜렷하지만, 강력한 현금흐름과 시장 지배력을 고려할 때 장기 보유 가치가 높다.',
        '투자자님의 과거 행동을 보면 단기 변동성에 민감하게 반응하여 조기 청산하는 패턴이 반복되었다. 이번에는 기업의 본질적 가치에 집중하여 장기 관점을 유지하시길 권한다.',
        '시장이 두려워할 때가 오히려 기회이다. 현재 삼성전자의 저평가 상황은 장기 투자자에게 매력적인 진입 시점이 될 수 있다.'
    ],
    graham: [
        '삼성전자의 PER 12배, PBR 1.2배는 반도체 업종 평균 대비 저평가되어 있으며, 충분한 안전마진을 제공한다.',
        '투자자님의 이전 투자 기록을 보면 안전마진이 부족한 상황에서 진입하여 손실을 경험한 사례가 있다. 이번에는 내재가치 대비 30% 이상 할인된 가격에서 분할 매수하시길 권한다.',
        '기술적 지표상 과매도 구간에서 반등 신호가 감지되며, 이는 안전마진 확보와 함께 매수 타이밍으로 적합하다.'
    ],
    wood: [
        'AI 시장 성장과 반도체 기술 혁신을 고려할 때, 삼성전자의 중장기 성장 잠재력은 매우 높다. 특히 메모리 반도체 분야의 기술적 우위가 돋보인다.',
        '투자자님의 포트폴리오를 보면 혁신 기술 분야에 과감하게 투자하는 성향이 있다. 이번에는 단기 변동성보다 장기 성장 트렌드에 집중하여 포지션을 구축하시길 권한다.',
        '장기 성장 트렌드를 반영한 기술적 지표가 긍정적이며, AI 시장 확장에 따른 수요 증가가 예상된다.'
    ],
    munger: [
        '삼성전자의 경제적 해자가 견고하며, 경영진의 주주친화적 정책이 긍정적이다. 글로벌 기술 트렌드 변화에도 잘 대응하고 있다.',
        '투자자님의 투자 철학을 분석한 결과, 다학제적 사고로 접근하는 경향이 있다. 이번에는 기업의 문화와 경영진의 철학을 중시하여 장기적 관점에서 평가하시길 권한다.',
        '기업 가치와 주가의 괴리가 점진적으로 축소되는 추세이며, 이는 기업의 본질적 가치가 시장에서 인정받고 있음을 의미한다.'
    ],
    dalio: [
        '글로벌 반도체 사이클과 환율 변동을 고려할 때, 현재는 매수 타이밍으로 보인다. 포트폴리오 분산 차원에서도 적절하다.',
        '투자자님의 과거 투자 패턴을 보면 단일 자산에 집중하는 경향이 있다. 이번에는 반도체 섹터 내에서도 분산 투자를 고려하시길 권한다.',
        '거시경제 지표와 연관된 기술적 신호가 긍정적이며, 글로벌 경제 회복에 따른 수요 증가가 예상된다.'
    ],
    lynch: [
        '우리 일상 속 모든 전자제품에 삼성의 기술이 들어있다. 이런 친숙한 기업에 투자하는 것은 피터 린치의 철학과 부합한다.',
        '투자자님의 소비 패턴을 보면 삼성 제품을 자주 사용하고 있다. 이번에는 소비자로서의 경험을 바탕으로 투자 결정을 내리시길 권한다.',
        '소비자 선호도와 브랜드 가치를 반영한 지표가 안정적이며, 일상에서 발견할 수 있는 투자 기회의 전형적 사례이다.'
    ]
};

export const DemoSectionDesktop: React.FC = () => {
    const [selectedMentor, setSelectedMentor] = useState('buffett');

    return (
        <DemoContainer data-section="demo">
            <DemoContent>
                <SectionTitle>
                    실제 분석 화면을 확인해보세요
                </SectionTitle>
                <SectionSubtitle>
                    삼성전자 투자 고민을 예시로 9명의 투자 멘토가
                    <br />
                    어떻게 다른 관점에서 분석하는지 경험해보세요.
                </SectionSubtitle>

                <DemoLayout>
                    <PhoneDemo>
                        <PhoneMockup>
                            <PhoneScreen>
                                <StatusBar>
                                    <Time>9:41</Time>
                                    <StatusIcons>
                                        <BatteryIcon />
                                    </StatusIcons>
                                </StatusBar>

                                <NavigationBar>
                                    <TabContainer>
                                        <Tab active={true}>
                                            <TabText active={true}>AI 리포트</TabText>
                                        </Tab>
                                        <Tab active={false}>
                                            <TabText active={false}>내 매매기록</TabText>
                                        </Tab>
                                    </TabContainer>
                                </NavigationBar>

                                <ContentContainer>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <SectionTitleMobile>고민 요약</SectionTitleMobile>

                                        <MentorQuoteCard>
                                            <MentorHeader>
                                                <MentorAvatar>{mentors.find(m => m.id === selectedMentor)?.icon}</MentorAvatar>
                                                <MentorName>{mentors.find(m => m.id === selectedMentor)?.name}</MentorName>
                                            </MentorHeader>
                                            <QuoteText>{mentors.find(m => m.id === selectedMentor)?.quote}</QuoteText>
                                        </MentorQuoteCard>

                                        <SummaryCard>
                                            <SummaryCardTitle>종합 판단 요약</SummaryCardTitle>
                                            <SummaryItem>✔️ {summaryData[selectedMentor as keyof typeof summaryData][0]}</SummaryItem>
                                            <Divider />
                                            <SummaryItem>✔️ {summaryData[selectedMentor as keyof typeof summaryData][1]}</SummaryItem>
                                            <Divider />
                                            <SummaryItem>✔️ {summaryData[selectedMentor as keyof typeof summaryData][2]}</SummaryItem>
                                        </SummaryCard>
                                    </div>
                                </ContentContainer>

                                <GestureBar>
                                    <GestureIndicator />
                                </GestureBar>
                            </PhoneScreen>
                        </PhoneMockup>
                    </PhoneDemo>

                    <DemoControls>
                        <ControlTitle>
                            멘토를 선택해서
                            <br />
                            다양한 관점을 확인하세요
                        </ControlTitle>

                        <MentorSelector>
                            {mentors.map(mentor => (
                                <MentorButton
                                    key={mentor.id}
                                    active={selectedMentor === mentor.id}
                                    onClick={() => setSelectedMentor(mentor.id)}
                                >
                                    <MentorButtonTitle>{mentor.name}</MentorButtonTitle>
                                    <MentorButtonDescription active={selectedMentor === mentor.id}>
                                        {mentor.shortDesc}
                                    </MentorButtonDescription>
                                </MentorButton>
                            ))}
                        </MentorSelector>
                    </DemoControls>
                </DemoLayout>
            </DemoContent>
        </DemoContainer>
    );
}; 